import React, { useCallback, useMemo } from 'react';

import Loading from 'components/invisible/Loading';
import { useGetEntitlements } from 'providers/entitlements/queries';
import { createSafeContext } from 'utils/createSafeContext';
import { Entitlement, EntitlementKey, EntitlementKeys } from './types';

export interface EntitlementsContext {
  /** Returns all entitlements */
  getEntitlements: () => Entitlement[];

  /** Returns a specific entitlement */
  getEntitlement: (key: EntitlementKey) => Entitlement | undefined;

  /** Returns true if the entitlement is enabled */
  isEntitledTo: (key: EntitlementKey) => boolean;
}

export const [Provider, useEntitlements] =
  createSafeContext<EntitlementsContext>(
    'EntitlementsContext',
    'useEntitlements must be a descendant of EntitlementsProvider',
  );

interface EntitlementsProviderProps {
  children?: React.ReactNode;
}

export function withCoreFiltersEntitlements<P extends object>(
  Component: React.ComponentType<P & { showCoreFiltersOnly?: boolean }>,
) {
  return (props: P) => {
    // Check to make sure the EntitlementsProvider has been loaded
    try {
      const { isEntitledTo } = useEntitlements();
      const showCoreFiltersOnly =
        isEntitledTo(EntitlementKeys.FB_FILTERS_PROFILE_CORE) &&
        !isEntitledTo(EntitlementKeys.FB_FILTERS_PROFILE_EXTENDED);
      return <Component {...props} showCoreFiltersOnly={showCoreFiltersOnly} />;
    } catch (error) {
      console.warn('Entitlements is not available.');
      return <Component {...props} showCoreFiltersOnly />;
    }
  };
}

export const EntitlementsProvider = ({
  children,
}: EntitlementsProviderProps) => {
  const { data, isLoading } = useGetEntitlements();

  const entitlements = useMemo(() => data?.entitlements ?? undefined, [data]);

  const getEntitlements = useCallback(
    () => Object.values(entitlements || {}),
    [entitlements],
  );

  const getEntitlement = useCallback(
    (key: EntitlementKey) => entitlements?.[key],
    [entitlements],
  );

  const isEntitledTo = useCallback(
    (key: EntitlementKey) => entitlements?.[key]?.enabled ?? false,
    [entitlements],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Provider
      value={{
        getEntitlements,
        getEntitlement,
        isEntitledTo,
      }}
    >
      {children}
    </Provider>
  );
};
